import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { ColorPickerHex } from "@/components/_molecules/color-picker/color-picker-hex";
import { type CreatedLabel, useCreateLabel } from "@/domain/label/hooks/api/use-create-label";
import { cn } from "@/lib/cn";

export interface LabelCreateFormProps {
  workspaceId: string;
  className?: string;
  labelGroupId?: string;
  defaultLabelColor?: string;
  onSuccess?: (label: CreatedLabel) => void;
  onCanceled?: () => void;
}

export const LabelCreateForm: FC<LabelCreateFormProps> = ({
  workspaceId,
  labelGroupId,
  onSuccess,
  onCanceled,
  className,
  ...props
}) => {
  const { t } = useTranslation("common");
  const ref = useRef<HTMLInputElement>(null);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState(props.defaultLabelColor ?? "#95999F");

  const { createLabel } = useCreateLabel({
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
  });

  async function handleCreateNewLabel() {
    if (newLabelName === "" || newLabelColor === "") {
      toast.error(t("errors.title"), { description: t("label.validationError") });
      return;
    }

    await createLabel({
      title: newLabelName,
      color: newLabelColor,
      workspaceId: workspaceId,
      labelGroupId: labelGroupId,
    });
  }

  function reset() {
    // do not reset the color; the most likely case is that users want to create several objects of the same color
    // setNewLabelColor("#95999F")
    setNewLabelName("");
    ref.current?.focus();
  }

  function onUpdateCompleted(input: CreatedLabel) {
    reset();
    if (onSuccess) {
      onSuccess(input);
    }
  }

  function onUpdateError() {
    toast.error(t("errors.title"), { description: t("errors.generic") });
  }

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div
      className={cn(["my-1 flex flex-row items-center justify-between gap-2 rounded-sm border bg-muted p-2", className])}
    >
      <ColorPickerHex value={newLabelColor} onChange={(color) => setNewLabelColor(color)} />
      <div className="grow">
        <Input
          ref={ref}
          autoFocus
          value={newLabelName}
          onChange={(e) => setNewLabelName(e.currentTarget.value)}
          placeholder={t("label.namePlaceholder")}
          aria-label={t("label.namePlaceholder")}
          className="bg-background focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
        />
      </div>
      <div>
        <Button variant="outline" size="sm" className="cursor-pointer" onClick={onCanceled} aria-label={t("actions.cancel")}>
          {t("actions.cancel")}
        </Button>
      </div>
      <div>
        <Button
          variant="default"
          size="sm"
          className="cursor-pointer"
          disabled={newLabelName === ""}
          onClick={handleCreateNewLabel}
          aria-label={t("actions.save")}
        >
          {t("actions.save")}
        </Button>
      </div>
    </div>
  );
};
